import type { RefObject } from '@mui/x-internals/types';
import {
  gridColumnLookupSelector,
  gridRowIdSelector,
  gridRowsLookupSelector,
  isAutogeneratedRow,
} from '@mui/x-data-grid-pro';
import type { GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';
import { getRowValue as getRowValueUtil } from '@mui/x-data-grid-pro/internals';
import type { DataGridPremiumProcessedProps } from '../../../models/dataGridPremiumProps';
import type { GridPrivateApiPremium } from '../../../models/gridApiPremium';
import type {
  GridComputedColumnDefinition,
  GridComputedColumnsModel,
  GridComputedColumnType,
} from '../computedColumns/gridComputedColumnsInterfaces';
import { gridComputedColumnsRevisionSelector } from '../computedColumns/gridComputedColumnsSelectors';
import {
  createFormulaCellKey,
  createFormulaError,
  evaluateFormula,
  extractFormulaDependencies,
  getFormulaExpression,
  toFormulaText,
} from './engine';
import type { FormulaErrorValue, FormulaScalar } from './engine';
import { EMPTY_POSITION_CONTEXT } from './gridFormulaPositionContext';
import {
  applyComputedColDefOverrides,
  areComputedColDefOverridesEqual,
  createComputedBaseColDef,
  resolveComputedColDefOverrides,
} from './createComputedColDef';
import type { GridComputedColumnGetters } from './createComputedColDef';
import type {
  GridComputedColumnRecord,
  GridComputedColumnsRuntimeCache,
  GridFormulaInternalCache,
  GridFormulaResult,
} from './gridFormulaInterfaces';

const EMPTY_RESULT: GridFormulaResult = { type: 'value', value: null };

const CYCLE_RESULT: GridFormulaResult = {
  type: 'error',
  code: '#CYCLE!',
  message: 'The formula is part of a circular reference.',
};

const MISSING_COLUMN_RESULT: GridFormulaResult = {
  type: 'error',
  code: '#REF!',
  message: 'The computed column does not exist.',
};

const RESULT_TYPE_LABELS: Record<Exclude<GridComputedColumnType, 'string'>, string> = {
  number: 'a number',
  boolean: 'a boolean',
  date: 'a date',
  dateTime: 'a date',
};

function toResolvedValue(result: GridFormulaResult): FormulaScalar | FormulaErrorValue {
  return result.type === 'error' ? createFormulaError(result.code, result.message) : result.value;
}

/**
 * The column is rendered, sorted, filtered and exported with the column type of
 * the definition, and the non-text types only accept values of their own type
 * (the date formatters throw on anything else), so a result of another type is
 * an error of the row. A bare field reference passes the row value through
 * untouched, so the gate is as strict as the engine's own: a number is finite,
 * a date is valid. Shared with the editor preview, which shows the result the
 * way the cell will.
 */
export function coerceComputedResult(
  result: GridFormulaResult,
  type: GridComputedColumnType,
): GridFormulaResult {
  if (result.type === 'error' || result.value === null) {
    return result;
  }
  const { value } = result;
  let matches: boolean;
  switch (type) {
    case 'number':
      matches = typeof value === 'number' && Number.isFinite(value);
      break;
    case 'boolean':
      matches = typeof value === 'boolean';
      break;
    case 'date':
    case 'dateTime':
      matches = value instanceof Date && !Number.isNaN(value.getTime());
      break;
    default: {
      if (typeof value === 'string') {
        return result;
      }
      const text = toFormulaText(value);
      return typeof text === 'string'
        ? { type: 'value', value: text }
        : { type: 'error', code: text.code, message: text.message };
    }
  }
  if (matches) {
    return result;
  }
  return {
    type: 'error',
    code: '#VALUE!',
    message: `The formula result is not ${RESULT_TYPE_LABELS[type as keyof typeof RESULT_TYPE_LABELS]}.`,
  };
}

/**
 * Whether a formula cell of the row the column reads is locked by a cycle. Like the
 * formulas that read the column, the column is then on the cycle or downstream of it,
 * whichever branch its formula takes: the dependencies are static.
 */
function isLockedByCycle(
  apiRef: RefObject<GridPrivateApiPremium>,
  cache: GridFormulaInternalCache,
  row: GridValidRowModel,
  field: string,
) {
  if (cache.cyclicKeys.size === 0) {
    return false;
  }
  const closure = cache.computedColumns.dependencyClosure.get(field);
  if (closure === undefined) {
    return false;
  }
  const id = gridRowIdSelector(apiRef, row);
  for (const dependency of closure) {
    if (cache.cyclicKeys.has(createFormulaCellKey(id, dependency))) {
      return true;
    }
  }
  return false;
}

/**
 * Evaluates one computed cell. The result is memoized by row identity, so each
 * (row, column) pair walks its AST once until the row or the definitions change.
 */
export function evaluateComputedCell(
  apiRef: RefObject<GridPrivateApiPremium>,
  row: GridValidRowModel,
  field: string,
): GridFormulaResult {
  const cache = apiRef.current.caches.formula;
  const record = cache?.computedColumns.records.get(field);
  if (cache === undefined || record === undefined) {
    return MISSING_COLUMN_RESULT;
  }
  if (isAutogeneratedRow(row)) {
    return EMPTY_RESULT;
  }
  if (record.staticResult !== null) {
    return record.staticResult;
  }

  const runtime = cache.computedColumns;
  let rowResults = runtime.results.get(row);
  if (rowResults === undefined) {
    rowResults = new Map();
    runtime.results.set(row, rowResults);
  } else if (rowResults.has(field)) {
    // `undefined` marks an evaluation in progress: the column depends on itself.
    return rowResults.get(field) ?? CYCLE_RESULT;
  }

  if (isLockedByCycle(apiRef, cache, row, field)) {
    rowResults.set(field, CYCLE_RESULT);
    return CYCLE_RESULT;
  }

  rowResults.set(field, undefined);
  let result: GridFormulaResult | undefined;
  try {
    const id = gridRowIdSelector(apiRef, row);
    const columnsLookup = gridColumnLookupSelector(apiRef);
    result = coerceComputedResult(
      evaluateFormula(record.ast!, {
        currentCell: { id, field },
        // Only same-row field references reach the resolver: every other kind of
        // reference makes the record static.
        getCellValue: (ref) => {
          const colDef = columnsLookup[ref.field];
          if (colDef === undefined) {
            return undefined;
          }
          if (colDef.computed) {
            return toResolvedValue(evaluateComputedCell(apiRef, row, ref.field));
          }
          // The records hold the freshest cell-formula results — the state lookup is
          // only written after a pass. A pass evaluates the formula cells a computed
          // cell reads before the formulas that read it, and drops the results of the
          // row each time one of them is evaluated.
          const formulaRecord = cache.records.get(createFormulaCellKey(id, ref.field));
          if (formulaRecord !== undefined) {
            return toResolvedValue(formulaRecord.result);
          }
          return getRowValueUtil(row, colDef, apiRef) as FormulaScalar;
        },
        hasRow: () => true,
        hasField: (referencedField) => columnsLookup[referencedField] !== undefined,
        position: EMPTY_POSITION_CONTEXT,
        functions: cache.registry,
      }),
      record.definition.type,
    );
  } finally {
    if (result === undefined) {
      rowResults.delete(field);
    }
  }
  rowResults.set(field, result);
  return result;
}

/**
 * The value a formula reads from a computed cell: the typed result, with an
 * error kept as an error value instead of the code string the `valueGetter`
 * returns to the grid. Static results are not memoized, so this is the read
 * path of the cell-formula runtime and of the draft preview.
 * @param {RefObject<GridPrivateApiPremium>} apiRef The private grid api.
 * @param {GridValidRowModel} row The row of the cell.
 * @param {string} field The field of the computed column.
 * @returns {FormulaScalar | FormulaErrorValue} The value of the cell as the engine sees it.
 */
export function resolveComputedCellValue(
  apiRef: RefObject<GridPrivateApiPremium>,
  row: GridValidRowModel,
  field: string,
): FormulaScalar | FormulaErrorValue {
  return toResolvedValue(evaluateComputedCell(apiRef, row, field));
}

function createComputedGetters(
  apiRef: RefObject<GridPrivateApiPremium>,
  field: string,
): GridComputedColumnGetters {
  const getValue = (row: GridValidRowModel) => {
    const result = evaluateComputedCell(apiRef, row, field);
    return result.type === 'error' ? result.code : result.value;
  };
  return {
    valueGetter: (value, row) => getValue(row),
    groupingValueGetter: (value, row) => getValue(row) as never,
    rowSpanValueGetter: (value, row) => getValue(row) as never,
  };
}

function areNumberFormatsEqual(
  a: Intl.NumberFormatOptions | undefined,
  b: Intl.NumberFormatOptions | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined) {
    return false;
  }
  const aKeys = Object.keys(a) as (keyof Intl.NumberFormatOptions)[];
  if (aKeys.length !== Object.keys(b).length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]);
}

function areDefinitionsEqual(
  a: GridComputedColumnDefinition,
  b: GridComputedColumnDefinition,
): boolean {
  return (
    a === b ||
    (a.field === b.field &&
      a.headerName === b.headerName &&
      a.formula === b.formula &&
      a.type === b.type &&
      a.description === b.description &&
      areNumberFormatsEqual(a.numberFormat, b.numberFormat))
  );
}

function buildComputedColumnRecord(
  apiRef: RefObject<GridPrivateApiPremium>,
  cache: GridFormulaInternalCache,
  definition: GridComputedColumnDefinition,
  previous: GridComputedColumnRecord | undefined,
  computedColDef: DataGridPremiumProcessedProps['computedColDef'],
): GridComputedColumnRecord {
  const formula = typeof definition.formula === 'string' ? definition.formula : '';
  const parse = cache.parser.parse(getFormulaExpression(formula));
  let dependencies = new Set<string>();
  let staticResult: GridFormulaResult | null = null;
  if (parse.ast === null) {
    staticResult = {
      type: 'error',
      code: '#ERROR!',
      message: parse.error?.message ?? 'The formula could not be parsed.',
    };
  } else {
    const staticDependencies = extractFormulaDependencies(parse.ast);
    dependencies = staticDependencies.fieldRefs;
    if (
      staticDependencies.usesPositionContext ||
      staticDependencies.cellRefs.length > 0 ||
      staticDependencies.ranges.length > 0 ||
      staticDependencies.columnValues.size > 0
    ) {
      staticResult = {
        type: 'error',
        code: '#REF!',
        message: 'Computed columns can only reference columns of the same row.',
      };
    }
  }

  // Same evaluation → same getters: the identity of `valueGetter` tells the
  // cell-formula runtime whether the values of the column may have changed.
  const evaluatesLikePrevious =
    previous !== undefined &&
    previous.definition.formula === definition.formula &&
    previous.definition.type === definition.type;
  const getters = evaluatesLikePrevious
    ? {
        valueGetter: previous.baseColDef.valueGetter!,
        groupingValueGetter: previous.baseColDef.groupingValueGetter!,
        rowSpanValueGetter: previous.baseColDef.rowSpanValueGetter!,
      }
    : createComputedGetters(apiRef, definition.field);

  // The cell renders as an error when the memoized result is one, not when a text
  // result reads like an error code.
  const isErrorRow = (row: GridValidRowModel) =>
    evaluateComputedCell(apiRef, row, definition.field).type === 'error';
  const baseColDef = createComputedBaseColDef(
    definition,
    getters,
    staticResult !== null,
    isErrorRow,
  );
  const overrides = resolveComputedColDefOverrides(definition, computedColDef);
  return {
    definition,
    ast: parse.ast,
    dependencies,
    staticResult,
    issues: [],
    isErrorRow,
    overrides,
    baseColDef,
    colDef: applyComputedColDefOverrides(baseColDef, definition, overrides, isErrorRow),
  };
}

function computeDependencyClosure(records: GridComputedColumnsRuntimeCache['records']) {
  const dependencyClosure = new Map<string, Set<string>>();
  const referencedFields = new Set<string>();
  for (const field of records.keys()) {
    const closure = new Set<string>();
    const stack = [field];
    while (stack.length > 0) {
      const dependencies = records.get(stack.pop()!)?.dependencies;
      if (dependencies !== undefined) {
        for (const dependency of dependencies) {
          if (!closure.has(dependency)) {
            closure.add(dependency);
            referencedFields.add(dependency);
            stack.push(dependency);
          }
        }
      }
    }
    dependencyClosure.set(field, closure);
  }
  return { dependencyClosure, referencedFields };
}

/**
 * Brings the records in line with the model. Only the records of the definitions
 * that changed are rebuilt, and the memoized results are only dropped when the
 * evaluation of a column changed.
 * @param {RefObject<GridPrivateApiPremium>} apiRef The private grid api.
 * @param {GridFormulaInternalCache} cache The internal cache of the formula feature.
 * @param {GridComputedColumnsModel} model The definitions to create records for.
 * @param {DataGridPremiumProcessedProps['computedColDef']} computedColDef The `computedColDef` prop.
 */
export function ensureComputedColumnRecords(
  apiRef: RefObject<GridPrivateApiPremium>,
  cache: GridFormulaInternalCache,
  model: GridComputedColumnsModel,
  computedColDef: DataGridPremiumProcessedProps['computedColDef'],
) {
  const runtime = cache.computedColumns;
  if (runtime.model === model && runtime.computedColDef === computedColDef) {
    return;
  }

  const overridesChanged = runtime.computedColDef !== computedColDef;
  const records: GridComputedColumnsRuntimeCache['records'] = new Map();
  let evaluationChanged = false;
  for (const definition of model) {
    const previous = runtime.records.get(definition.field);
    let record: GridComputedColumnRecord;
    if (previous !== undefined && areDefinitionsEqual(previous.definition, definition)) {
      record = previous;
      // The `editable` flag does not change the column, but a `computedColDef` function
      // can read it, so it receives the new definition.
      const definitionChanged = previous.definition.editable !== definition.editable;
      if (overridesChanged || definitionChanged) {
        // An inline prop is a new object on every render: the column is only re-created
        // when the overrides of the definition changed.
        const overrides = resolveComputedColDefOverrides(definition, computedColDef);
        if (!areComputedColDefOverridesEqual(overrides, previous.overrides)) {
          record = {
            ...previous,
            definition,
            overrides,
            colDef: applyComputedColDefOverrides(
              previous.baseColDef,
              definition,
              overrides,
              previous.isErrorRow,
            ),
          };
        } else if (definitionChanged) {
          record = { ...previous, definition };
        }
      }
    } else {
      record = buildComputedColumnRecord(apiRef, cache, definition, previous, computedColDef);
      if (record.baseColDef.valueGetter !== previous?.baseColDef.valueGetter) {
        evaluationChanged = true;
      }
    }
    records.set(definition.field, record);
  }
  for (const field of runtime.records.keys()) {
    if (!records.has(field)) {
      evaluationChanged = true;
    }
  }

  runtime.records = records;
  runtime.model = model;
  runtime.computedColDef = computedColDef;
  if (evaluationChanged) {
    const { dependencyClosure, referencedFields } = computeDependencyClosure(records);
    runtime.dependencyClosure = dependencyClosure;
    runtime.referencedFields = referencedFields;
    resetComputedResults(cache);
  }
}

/**
 * Tells whether a new `computedColDef` prop changes the overrides of a computed column,
 * so that the columns must be hydrated again to apply it.
 * @param {GridFormulaInternalCache} cache The internal cache of the formula feature.
 * @param {DataGridPremiumProcessedProps['computedColDef']} computedColDef The `computedColDef` prop.
 * @returns {boolean} `true` when the overrides of at least one record differ.
 */
export function haveComputedColDefOverridesChanged(
  cache: GridFormulaInternalCache,
  computedColDef: DataGridPremiumProcessedProps['computedColDef'],
): boolean {
  const runtime = cache.computedColumns;
  if (runtime.computedColDef === computedColDef) {
    return false;
  }
  for (const record of runtime.records.values()) {
    const overrides = resolveComputedColDefOverrides(record.definition, computedColDef);
    if (!areComputedColDefOverridesEqual(overrides, record.overrides)) {
      return true;
    }
  }
  return false;
}

export function resetComputedResults(cache: GridFormulaInternalCache) {
  cache.computedColumns.results = new WeakMap();
}

/**
 * Drops the memoized results of a row while a cell-formula pass evaluates one of its
 * formula cells that a computed column reads.
 * @param {GridFormulaInternalCache} cache The internal cache of the formula feature.
 * @param {GridValidRowModel | undefined} row The row of the evaluated formula cell.
 */
export function evictComputedResultsForRow(
  cache: GridFormulaInternalCache,
  row: GridValidRowModel | undefined,
) {
  if (row !== undefined) {
    cache.computedColumns.results.delete(row);
  }
}

/**
 * Drops the memoized results of rows whose object did not change but whose
 * inputs did — the cell formulas of the row were re-evaluated.
 */
export function evictComputedResultsForRows(
  apiRef: RefObject<GridPrivateApiPremium>,
  ids: Iterable<GridRowId>,
) {
  const cache = apiRef.current.caches.formula;
  if (cache === undefined) {
    return;
  }
  const rowsLookup = gridRowsLookupSelector(apiRef);
  for (const id of ids) {
    const row = rowsLookup[id];
    if (row !== undefined) {
      cache.computedColumns.results.delete(row);
    }
  }
}

/**
 * Makes the computed cells read their value again.
 */
export function bumpComputedColumnsRevision(apiRef: RefObject<GridPrivateApiPremium>) {
  const revision = gridComputedColumnsRevisionSelector(apiRef);
  apiRef.current.setState((state) => ({
    ...state,
    computedColumns: { ...state.computedColumns, revision: revision + 1 },
  }));
}
